/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import * as Yup from "yup";

import { Stack, StackItem, Text } from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";

import TextFieldController from "Components/ReactHookForm/TextFieldController/TextFieldController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { OperationStatus } from "Constants/OperationStatus";
import { flagApplication, flagApplicationReset } from "Dataflows/Application/ApplicationAction";
import {
  getFlagApplicationErrorMessage,
  getFlagApplicationStatus,
} from "Dataflows/Application/ApplicationSelector";

import {
  flexColumnStackStyles,
  tfaTextStyles,
  tfaTitleStyles,
} from "./ApplicationFlaggingSidePanel.styles";
import { IApplicationFlaggingSidePanelProps } from "./IApplicationFlaggingSidePanel";
import ComboBoxController from "Components/ReactHookForm/ComboBoxController/ComboBoxController";
import { REASON_FOR_FLAG_DROPDOWN_OPTIONS } from "Constants/ApplicationConstants";

export const ApplicationFlaggingSidePanel = (
  props: IApplicationFlaggingSidePanelProps
): ReactElement => {
  const { isPanelOpen, setIsPanelOpen, applicationFormId, applicationTechnologyFocusAreas } = props;
  const dispatch = useDispatch();

  const flagApplicationErrorMessage = useSelector(getFlagApplicationErrorMessage);
  const flagApplicationStatus = useSelector(getFlagApplicationStatus);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        justification: Yup.string().required("Justification is required").max(3900),
        flagReasonId: Yup.string().required("Please select the reason"),
      })
    ),
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const save = () =>
    handleSubmit((formData) => {
      dispatch(flagApplication(applicationFormId, formData.justification, formData.flagReasonId));
    });

  const dismissPanel = (): void => {
    setIsPanelOpen(false);
    dispatch(flagApplicationReset());
    reset();
  };

  useEffect(() => {
    if (flagApplicationStatus === OperationStatus.Succeeded) {
      dismissPanel();
    }
  }, [flagApplicationStatus]);

  /**
   * filtered out default record(any)
   */
  const flagReasonOption = REASON_FOR_FLAG_DROPDOWN_OPTIONS.filter((option: any) => option.key);

  return (
    <form onSubmit={save()}>
      <SidePanel
        isOpen={isPanelOpen}
        title="Flag for Additional Review"
        saveButtonLabel="Save"
        error={flagApplicationErrorMessage}
        disableActions={flagApplicationStatus === OperationStatus.Pending}
        onDismiss={dismissPanel}
        onSave={save()}
      >
        <Stack tokens={{ childrenGap: "1rem" }}>
          {applicationTechnologyFocusAreas &&
            applicationTechnologyFocusAreas.length > 0 &&
            applicationTechnologyFocusAreas.map((tfa) => (
              <StackItem
                key={applicationTechnologyFocusAreas.indexOf(tfa)}
                styles={flexColumnStackStyles}
              >
                <Text styles={tfaTitleStyles}>{tfa.technologyFocusAreaName}</Text>
                <Text styles={tfaTextStyles}>Score: {tfa.technologyFocusAreaScore}</Text>
                {tfa.technologyFocusAreaStatus && (
                  <Text styles={tfaTextStyles}>
                    Status: {tfa.technologyFocusAreaStatus.replace(/-/g, " ")}
                  </Text>
                )}
              </StackItem>
            ))}
          <StackItem>
            <ComboBoxController
              control={control}
              errors={errors}
              placeholderText="Pick reason from the drop-down list"
              inputName="flagReasonId"
              isRequired={true}
              labelDisplayName="Reason for Flag"
              options={flagReasonOption}
            />
          </StackItem>
          <StackItem>
            <TextFieldController
              inputName="justification"
              control={control}
              errors={errors}
              isRequired
              isMultiline
              hasTooltip
              labelDisplayName="Justification"
              toolTipDisplayText="Please explain why this application needs additional review"
              characterCountLimit={3900}
            />
          </StackItem>
        </Stack>
      </SidePanel>
    </form>
  );
};
